import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const About2 = ({ isAdmin = false, onEditClick }) => {
  const [aboutData, setAboutData] = useState({
    heading: "Our Care Philosophy",
    subheading: "At WarmWellness, we've reimagined healthcare as it should be - compassionate, comprehensive, and centered on you.",
    features: [],
    stats: [],
    buttonText: "Experience the WarmWellness Difference",
    buttonLink: "/our-approach",
    colors: {
      primary: "#0760ad",
      primaryLight: "#098a48",
      secondary: "#118a94",
      secondaryLight: "#339166",
      white: "#FFFFFF",
      lightBg: "#f8fafc"
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/about/about2/`);
        if (response.ok) {
          const data = await response.json();
          setAboutData({
            heading: data.heading || "Our Care Philosophy",
            subheading: data.subheading || "At WarmWellness, we've reimagined healthcare...",
            features: data.features || [],
            stats: data.stats || [],
            buttonText: data.buttonText || "Experience the WarmWellness Difference",
            buttonLink: data.buttonLink || "/our-approach",
            colors: data.colors || {
              primary: "#0760ad",
              primaryLight: "#098a48",
              secondary: "#118a94",
              secondaryLight: "#339166",
              white: "#FFFFFF",
              lightBg: "#f8fafc"
            }
          });
        }
      } catch (error) {
        console.error('Error fetching about2 data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (isLoading) {
    return (
      <div className="w-full py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto flex justify-center">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-16 px-4 sm:px-6 lg:px-8 relative" style={{ backgroundColor: aboutData.colors.lightBg }}>
      {isAdmin && (
        <button 
          onClick={onEditClick}
          className="absolute top-4 right-4 btn btn-sm btn-primary z-10"
        >
          Edit Section
        </button>
      )}

      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: aboutData.colors.primary }}>
            {aboutData.heading}
          </h2>
          <div className="w-24 h-1 mx-auto mb-6 rounded-full" style={{ backgroundColor: aboutData.colors.secondary }}></div>
          <p className="max-w-3xl mx-auto text-lg" style={{ color: aboutData.colors.primary }}>
            {aboutData.subheading}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {aboutData.features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all"
              style={{ borderTop: `4px solid ${feature.color || aboutData.colors.primary}` }}
            >
              {feature.icon_type === 'image' ? (
                <img 
                  src={feature.icon} 
                  alt={feature.title}
                  className="w-12 h-12 mb-4 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/48?text=Icon';
                  }}
                />
              ) : (
                <div className="text-4xl mb-4">{feature.icon}</div>
              )}
              <h3 className="text-xl font-semibold mb-3" style={{ color: aboutData.colors.primary }}>
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 shadow-sm mb-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {aboutData.stats.map((stat, index) => (
              <div key={index}>
                <p className="text-4xl font-bold mb-2" style={{ color: stat.color || aboutData.colors.primary }}>
                  {stat.value}
                </p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {aboutData.buttonText && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.a
              href={aboutData.buttonLink}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="inline-block px-8 py-3 rounded-full font-medium text-white shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: aboutData.colors.primary }}
            >
              {aboutData.buttonText}
              <span className="ml-2">→</span>
            </motion.a>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default About2;